'use client';

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ChevronDownIcon } from './Icons';
import type { SidebarGroup } from './RulesSidebar';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function RuleSelectMobile({
  label,
  groups,
  activeSlug,
  onSelect,
}: {
  label?: string;
  groups: SidebarGroup[];
  activeSlug: string;
  onSelect: (slug: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const active = groups.flatMap((g) => g.rules).find((r) => r.slug === activeSlug);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={rootRef} data-ui="rules-v3.rule-select" className="relative z-20 flex w-full flex-col gap-[1.5rem]">
      <p className="mb-0 font-instrument-sans text-[1.75rem] font-medium uppercase leading-[2.5rem] text-black">
        {label ?? 'Rule'}
      </p>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-[0.5rem] border border-[#e5e5e5] bg-white p-[1.875rem] text-left"
      >
        <span className="min-w-0 flex-1 font-geist text-[2.25rem] font-medium leading-[1.01] tracking-[-0.0675rem] text-black">
          {active?.title}
        </span>
        <ChevronDownIcon
          className={cn('h-[0.75rem] w-[1.375rem] shrink-0 text-black transition-transform', open && 'rotate-180')}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease }}
            className="absolute left-0 top-full z-20 mt-[1rem] flex max-h-[60svh] w-full flex-col gap-[2rem] overflow-y-auto rounded-[0.5rem] border border-[#e5e5e5] bg-white px-[1.375rem] py-[2rem] shadow-[0_1.5rem_4rem_rgba(0,0,0,0.08)]"
          >
            {groups.map((group) => (
              <div key={group.key} className="flex flex-col gap-[1rem]">
                {group.title && (
                  <p className="mb-0 px-[1.5rem] font-geist text-[1.75rem] font-medium uppercase leading-[2.5rem] text-black/40">
                    {group.title}
                  </p>
                )}
                <ul className="m-0 flex list-none flex-col gap-[0.25rem] p-0">
                  {group.rules.map((rule) => {
                    const isActive = rule.slug === activeSlug;
                    return (
                      <li key={rule.slug}>
                        <button
                          type="button"
                          onClick={() => {
                            onSelect(rule.slug);
                            setOpen(false);
                          }}
                          className={cn(
                            'relative flex h-[5rem] w-full items-center rounded-[0.25rem] px-[1.5rem] text-left font-geist text-[2rem] leading-[3rem]',
                            isActive ? 'bg-[#f2f4f3] font-medium text-black' : 'text-black/[0.64]',
                          )}
                        >
                          {isActive && (
                            <span className="absolute left-0 top-[1.25rem] h-[2.5rem] w-[0.25rem] bg-[#228e71]" aria-hidden />
                          )}
                          {rule.title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
